import { Component, Input, Output, EventEmitter, OnInit, HostListener } from '@angular/core';
import * as moment from 'moment';

@Component({
  selector: 'app-week-picker',
  templateUrl: './week-picker.component.html',
  styleUrls: ['./week-picker.component.scss']
})
export class WeekPickerComponent implements OnInit {
  @Input() startDate: moment.Moment;
  @Input() endDate: moment.Moment;
  @Output() weekSelected = new EventEmitter<{ start: moment.Moment; end: moment.Moment }>();

  isOpen = false;
  viewMonth: moment.Moment;
  calendarWeeks: moment.Moment[][] = [];
  activePreset = '';
  hoveredDate: moment.Moment = null;

  readonly monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                         'July', 'August', 'September', 'October', 'November', 'December'];

  get displayText(): string {
    if (!this.startDate || !this.endDate) { return ''; }
    return `${this.startDate.format('DD MMM YYYY')} - ${this.endDate.format('DD MMM YYYY')}`;
  }

  get monthLabel(): string {
    return this.viewMonth ? this.viewMonth.format('MMMM YYYY') : '';
  }

  get yearOptions(): number[] {
    const y = moment().year();
    return Array.from({ length: 6 }, (_, i) => y - 2 + i);
  }

  ngOnInit() {
    this.viewMonth = this.startDate
      ? this.startDate.clone().startOf('month')
      : moment().startOf('month');
    this.buildCalendar();
    this.detectActivePreset();
  }

  private getWeekBounds(day: moment.Moment): { start: moment.Moment; end: moment.Moment } {
    const daysSinceFriday = (day.day() + 2) % 7;
    const start = day.clone().subtract(daysSinceFriday, 'days').startOf('day');
    const end = start.clone().add(6, 'days').endOf('day');
    return { start, end };
  }

  private detectActivePreset() {
    if (!this.startDate || !this.endDate) { return; }
    const { start: ts, end: te } = this.getWeekBounds(moment());
    const { start: ps, end: pe } = this.getWeekBounds(moment().subtract(7, 'days'));
    if (this.startDate.isSame(ts, 'day') && this.endDate.isSame(te, 'day')) {
      this.activePreset = 'this';
    } else if (this.startDate.isSame(ps, 'day') && this.endDate.isSame(pe, 'day')) {
      this.activePreset = 'past';
    } else {
      this.activePreset = '';
    }
  }

  buildCalendar() {
    const first = this.viewMonth.clone().startOf('month');
    const calStart = first.clone().subtract(first.day(), 'days');
    this.calendarWeeks = [];
    for (let w = 0; w < 6; w++) {
      const week: moment.Moment[] = [];
      for (let d = 0; d < 7; d++) {
        week.push(calStart.clone().add(w * 7 + d, 'days'));
      }
      this.calendarWeeks.push(week);
    }
  }

  selectDay(day: moment.Moment) {
    const { start, end } = this.getWeekBounds(day);
    this.startDate = start;
    this.endDate = end;
    this.hoveredDate = null;
    this.detectActivePreset();
    this.weekSelected.emit({ start, end });
    this.isOpen = false;
  }

  selectPreset(type: 'this' | 'past') {
    const { start, end } = this.getWeekBounds(
      type === 'this' ? moment() : moment().subtract(7, 'days')
    );
    this.startDate = start;
    this.endDate = end;
    this.activePreset = type;
    this.weekSelected.emit({ start, end });
    this.isOpen = false;
  }

  prevMonth() {
    this.viewMonth = this.viewMonth.clone().subtract(1, 'month');
    this.buildCalendar();
  }

  nextMonth() {
    this.viewMonth = this.viewMonth.clone().add(1, 'month');
    this.buildCalendar();
  }

  onMonthSelect(e: Event) {
    const m = parseInt((e.target as HTMLSelectElement).value, 10);
    this.viewMonth = this.viewMonth.clone().month(m);
    this.buildCalendar();
  }

  onYearSelect(e: Event) {
    const y = parseInt((e.target as HTMLSelectElement).value, 10);
    this.viewMonth = this.viewMonth.clone().year(y);
    this.buildCalendar();
  }

  isInSelectedWeek(day: moment.Moment): boolean {
    if (!this.startDate || !this.endDate) { return false; }
    return day.isBetween(this.startDate, this.endDate, 'day', '[]');
  }

  isInHoveredWeek(day: moment.Moment): boolean {
    if (!this.hoveredDate) { return false; }
    const { start, end } = this.getWeekBounds(this.hoveredDate);
    return day.isBetween(start, end, 'day', '[]');
  }

  isWeekEdge(day: moment.Moment, edge: 'start' | 'end'): boolean {
    const ref = edge === 'start' ? this.startDate : this.endDate;
    return ref ? day.isSame(ref, 'day') : false;
  }

  isOtherMonth(day: moment.Moment): boolean {
    return !day.isSame(this.viewMonth, 'month');
  }

  isToday(day: moment.Moment): boolean {
    return day.isSame(moment(), 'day');
  }

  toggleDropdown() {
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      this.viewMonth = this.startDate
        ? this.startDate.clone().startOf('month')
        : moment().startOf('month');
      this.buildCalendar();
    }
  }

  @HostListener('document:click', ['$event'])
  onDocClick(e: MouseEvent) {
    if (!(e.target as HTMLElement).closest('.week-picker-wrap')) {
      this.isOpen = false;
    }
  }
}
